import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { AppStateInterface } from "../appState.interface";
import { Injectable } from "@angular/core";
import {
  invokeFranchisedCompanyNamesByFranchise,
  invokeFranchiseInfo,
  invokeFranchiseList,
  invokeMainDashboard,
  invokeTopFranchised,
  resultMainDashboard,
  resultTopFranchised,
  invokePaymentDetails,
  invokeUpdateFranchiseProfile,
  resultFranchisedCompanyNamesByFranchise,
  resultFranchiseInfo,
  resultFranchiseList,
  resultPaymentDetails,
  setUpdateProfileSuccess,
  createFranchiseSuccess,
  getFranchisesSuccess,
  invokeCreateFranchise,
  invokeFranchisesCompanyNames,
  setFranchisesCompanyNames,
  invokeGetFranchiseInfoByCompanyName,
  invokeUpdateFranchise,
  updateFranchiseSuccess,
  invokeDeleteFranchiseAndSomeLocations,
  setDeleteFranchiseSuccess,
  invokeLocationsByFranchise,
  resultFranchisedsByFranchise,
  invokeCompareLocations,
  resultCompareLocations,
  invokeFranchiseFilter,
} from "./franchise.action";
import { catchError, map, mergeMap, of, Subject, withLatestFrom } from "rxjs";
import { FranchiseProfileInterface } from "src/app/shared/models/franchise/franchise-profile.interface";
import { setAPIStatus } from "../apiState.interface";
import { FranchiseService } from "src/app/shared/services/franchise.service";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { FranchiseMainDashboardDto } from "src/app/shared/models/franchise/FranchiseMainDashboardDto";
import { TopFranchisedResult } from "src/app/shared/models/franchise/topFranchised.interface";
import { PaymentDetails } from "src/app/shared/models/franchise/paymentDetails.interface";
import { FranchiseResultInterface } from "src/app/shared/models/franchise/franchise-result.interface";
import { LocationPartnersResultInterface } from "src/app/shared/models/location-partners/locationPartnersResult.interface";
import { ComparatorSummary } from "src/app/shared/models/franchise/ComparatorSummary";
import { locationPartnerFilterSelector } from "../locationPartner/location-partner.selectors";

@Injectable()
export class FranchiseEffect {
  private destroy$ = new Subject<void>();
  constructor(
    private actions$: Actions,
    private franchiseService: FranchiseService,
    private store: Store<AppStateInterface>
  ) {}

  invokeFranchiseInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeFranchiseInfo),
      mergeMap((state) => {
        return this.franchiseService.getFranchiseInfo().pipe(
          map((data: FranchiseProfileInterface | null) =>
            resultFranchiseInfo({ franchiseInfo: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeFranchisedCompanyNamesByFranchise$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeFranchisedCompanyNamesByFranchise),
      mergeMap((state) => {
        return this.franchiseService
          .getFranchisedCompanyNamesByFranchise(state.companyName)
          .pipe(
            map((data: string[]) =>
              resultFranchisedCompanyNamesByFranchise({
                franchisedNamesList: data,
              })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );
  invokeUpdateFranchiseProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeUpdateFranchiseProfile),
      mergeMap((state) => {
        return this.franchiseService.updateFranchiseProfile(state.profile).pipe(
          map((data: SuccessDto) => setUpdateProfileSuccess({ success: data })),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeMainDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeMainDashboard),
      mergeMap((state) => {
        return this.franchiseService.getMainDashboard(state.refreshCache).pipe(
          map((data: FranchiseMainDashboardDto) =>
            resultMainDashboard({ franchiseMainDashboardDto: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokePaymentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokePaymentDetails),
      mergeMap((state) => {
        return this.franchiseService.paymentDetails().pipe(
          map((data: PaymentDetails | null) =>
            resultPaymentDetails({
              paymentDetails: data,
            })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeTopFranchised$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeTopFranchised),
      mergeMap((state) => {
        return this.franchiseService
          .getTopFranchised(state.refreshCache, state.request)
          .pipe(
            map((data: TopFranchisedResult | null) =>
              resultTopFranchised({ topFranchised: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeFranchiseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeFranchiseList),
      mergeMap((state) => {
        return this.franchiseService.getFranchiseList().pipe(
          map((data: string[]) => resultFranchiseList({ franchiseList: data })),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeCreateFranchise$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeCreateFranchise),
      mergeMap((state) => {
        return this.franchiseService
          .createFranchise(state.createFranchise)
          .pipe(
            map((data: SuccessDto) =>
              createFranchiseSuccess({ successMessage: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeFranchisesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeFranchiseFilter),
      mergeMap((data) => {
        return this.franchiseService
          .getAllFranchisesByFilter(data.franchiseFilter)
          .pipe(
            map((data: FranchiseResultInterface) =>
              getFranchisesSuccess({ franchisesResult: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeGetAllFranchiseCampanyNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeFranchisesCompanyNames),
      mergeMap((state) => {
        return this.franchiseService.getAllFranchisesCompanyNames().pipe(
          map((data: string[] | null) =>
            setFranchisesCompanyNames({ companyNames: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeGetFranchiseInfoByCompanyName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeGetFranchiseInfoByCompanyName),
      mergeMap((state) => {
        return this.franchiseService
          .getFranchiseInfoByCompanyName(state.companyName)
          .pipe(
            map((data: FranchiseProfileInterface | null) =>
              resultFranchiseInfo({ franchiseInfo: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeUpdateFranchise$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeUpdateFranchise),
      mergeMap((state) => {
        return this.franchiseService
          .updateFranchiseFullProfile(state.franchiseDto, state.email)
          .pipe(
            map((data: SuccessDto) =>
              updateFranchiseSuccess({ success: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeDeleteFranchiseAndSomeLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeDeleteFranchiseAndSomeLocations),
      mergeMap((state) => {
        return this.franchiseService
          .deleteFranchiseAndSomeLocations(state.franchise, state.locations)
          .pipe(
            map((data: SuccessDto) =>
              setDeleteFranchiseSuccess({ success: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeLocationsByFranchise$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeLocationsByFranchise),
      withLatestFrom(this.store.pipe(select(locationPartnerFilterSelector))),
      mergeMap(([state, locationPartnerFilter]) => {
        return this.franchiseService
          .getFranchisedsByFranchiseCompanyName(
            state.companyName,
            locationPartnerFilter
          )
          .pipe(
            map((data: LocationPartnersResultInterface | null) =>
              resultFranchisedsByFranchise({ franchisedList: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeCompareLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeCompareLocations),
      mergeMap((state) => {
        return this.franchiseService.compareLocations(state.data).pipe(
          map((data: ComparatorSummary[] | null) =>
            resultCompareLocations({ comparatorResults: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );
}
